import { useState, useContext, useEffect, useRef } from "react";
import Pagination from "../Pagination/Pagination";
import moment from "moment";
import loaderContext from "../../context/LoaderContext";
import { Link } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/baseUrl";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import avtarImg from "../../assets/images/dummy-prod.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

export default function StudentListTable() {
  const [userList, setUserList] = useState([]);
  const [paginationDetails, setPaginationDetails] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [searchData, setSearchData] = useState({
    is_staff: "",
    name: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const fileInputRef = useRef(null);

  const { setLoader } = useContext(loaderContext);

  // Fetch when page number or filters change
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchLiveData();
  }, [pageNumber, pageLimit, debouncedSearchTerm]);

  const filterHandler = (e) => {
    setPageNumber(1);
    setSearchData((curr) => ({ ...curr, [e.target.name]: e.target.value }));
  };

  const fetchLiveData = async () => {
    try {
      setLoader(true);
      const response = await axios.get(`${baseUrl}/user/user-list`, {
        params: {
          search: debouncedSearchTerm,
          page: pageNumber,
          limit: pageLimit,
          name: searchData.name || "",
        },
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      });

      const { data, pagination } = response.data;
      setUserList(data);
      setPaginationDetails({
        currentPage: pagination.currentPage,
        totalPages: pagination.totalPages,
        totalItems: pagination.totalUsers,
      });
    } catch (error) {
      console.error("Error fetching user list:", error);
    } finally {
      setLoader(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${baseUrl}/api/v1/admin/user/user-delete/${id}`, {
            headers: { Authorization: localStorage.getItem("token") },
          });
          toast.success("User deleted successfully");
          fetchLiveData();
        } catch (err) {
          toast.error("Failed to delete user");
        }
      }
    });
  };

  const handleStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      await axios.patch(
        `${baseUrl}/user/user-status/${id}`,
        { status: newStatus },
        {
          headers: { Authorization: localStorage.getItem("token") },
        }
      );
      setUserList((prev) =>
        prev.map((user) =>
          user._id === id ? { ...user, status: newStatus } : user
        )
      );
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const handleUploadFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("excel", file);
    fileInputRef.current.value = "";

    uploadFile(formData);
  };

  const uploadFile = async (file) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/v1/admin/user/bulk-upload`,
        file,
        {
          headers: { Authorization: localStorage.getItem("token") },
        }
      );

      if (response.data.status) {
        fetchLiveData();
        Swal.fire({
          title: "Data uploaded!",
          icon: "success",
          draggable: true,
        });
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  return (
    <div className="cards user-list">
      <div className="bus-filter">
        <div className="row align-items-center">
          <div className="col-lg-6">
            <h5 className="card-title">
              Total Students ({paginationDetails?.totalItems})
            </h5>
          </div>
        </div>
        <div className="search-filter-container mt-2">
          <div className="search-container">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search Tenure..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-lg-12">
          <div className="table table-responsive custom-table management-table">
            <table className="table table-borderless">
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Image</th>
                  <th>User Code</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile Number</th>
                  <th>Joined At</th>
                </tr>
              </thead>
              <tbody>
                {userList.length > 0 ? (
                  userList.map((user, index) => (
                    <tr key={user._id}>
                      <td>
                        {(paginationDetails.currentPage - 1) * pageLimit +
                          index +
                          1}
                      </td>
                      <td>
                        <img
                          src={
                            user?.image
                              ? `${baseUrl}/uploads/${user.image}`
                              : avtarImg
                          }
                          alt="User"
                          onError={(e) => (e.target.src = avtarImg)}
                          style={{ width: "50px" }}
                        />
                      </td>
                      <td>{user?.userCode}</td>
                      <td className="text-capitalize">{user?.name}</td>
                      <td>{user?.email || "N/A"}</td>
                      <td>{user?.mobile || "N/A"}</td>
                      <td>{moment(user?.createdAt).format("lll")}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination Component */}
            {paginationDetails.totalPages > 1 && (
              <Pagination
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                totalPages={paginationDetails.totalPages}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
